import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MButton, MSelect, MTextArea } from '@Components/Base';
import { Flex, Typography } from 'antd';
import { FormikProps } from 'formik';
import { useGetTenderApproversQuery } from '@Features/MaterialRequest/requests/TenderRequest/store/api/TenderRequestApiSlice';
import { DefaultOptionType } from 'antd/es/select';
import { MobileDrawer } from '@Components/index';
import { initFormikValuesType } from '../../../types';

import styles from './approve-modal-mobile.module.css';
import { transformResponseToOptionGroups } from '../utils';

interface ApproveModalMobileProps {
  isModalOpen: boolean;
  formik: FormikProps<initFormikValuesType>;
  handleApprove: () => void;
  handleReject: () => void;
  handleCancel: () => void;
  hideApproverSelection: boolean;
}

const ApproveModalMobile: React.FC<ApproveModalMobileProps> = ({
  formik,
  handleCancel,
  handleApprove,
  handleReject,
  isModalOpen,
  hideApproverSelection,
}) => {
  const { tenderId } = useParams();
  const { data } = useGetTenderApproversQuery(Number(tenderId));
  const [approversOptions, setApproversOptions] = useState<
    Array<DefaultOptionType>
  >([]);

  useEffect(() => {
    if (data) {
      const optionGroups = transformResponseToOptionGroups(data);
      setApproversOptions(optionGroups || []);
    }
  }, [data]);

  const customButtons = useMemo(() => {
    return [
      <Flex justify="space-between">
        <MButton key="back" id="cancel" type="tertiary" onClick={handleCancel}>
          Cancel
        </MButton>

        <MButton key="reject" danger onClick={handleReject}>
          Reject
        </MButton>

        <MButton key="submit" id="ok" type="primary" onClick={handleApprove}>
          Approve
        </MButton>
      </Flex>,
    ];
  }, [handleApprove, handleCancel, handleReject]);

  const showApproverSelector = useMemo(() => {
    return (
      !hideApproverSelection &&
      Boolean(
        data?.approvers || data?.commercialApprovers || data?.technicalApprovers
      )
    );
  }, [
    data?.approvers,
    data?.commercialApprovers,
    data?.technicalApprovers,
    hideApproverSelection,
  ]);

  return (
    <MobileDrawer
      open={isModalOpen}
      title="Approval Confirmation"
      onClose={handleCancel}
      footer={customButtons}
    >
      <Flex vertical className={styles.modal}>
        <div className={styles.fieldsSection}>
          <div>
            You&apos;re about to approve your vendors selection. Please add any
            comment before finalizing your approval.
          </div>
          {showApproverSelector && (
            <div>
              <Typography.Text>
                Please choose the respective Approver:
              </Typography.Text>
              <MSelect
                style={{ width: '100%' }}
                options={approversOptions}
                onChange={(value) => {
                  formik.setFieldValue('approver', String(value));
                }}
                placeholder="Choose Approver"
              />
            </div>
          )}
          <div>
            <Typography.Text>Comments:</Typography.Text>
            <MTextArea
              name="comments"
              onChange={(input) =>
                formik.setFieldValue('comment', input.target.value)
              }
              rows={4}
              showCount
              maxLength={100}
              placeholder="Insert comment"
            />
          </div>
        </div>
      </Flex>
    </MobileDrawer>
  );
};

export default ApproveModalMobile;
